import { Box, Button, MenuItem, Select, Typography } from "@mui/material";

import { Paper } from "@mui/material";
import { ReactNode } from "react";
import { SecurityPipelineScheduledWorkflowCadence } from "~/operations";
import { SecurityPipelineGithubPageTabHeading } from "../SecurityPipelineGithubPageTabHeading/SecurityPipelineGithubPageTabHeading";
import { Flex } from "~/components/Flex";
import { SelectOptions } from "~/types/form";
import { SecurityPipelineGithubAutomateScope } from "./SecurityPipelineGithubAutomateScope";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { SecurityPipelineGithubAutomateFormValues } from "./types";
import { Format } from "~/lib/date";
import { LoadingButton } from "~/components/loading-button";

// Constants for default day values
const DEFAULT_WEEKLY_DAY = 0; // Monday
const DEFAULT_MONTHLY_DAY = 1; // 1st of month

interface SecurityPipelineGithubScheduleProps {
  cadenceOptions: SelectOptions<SecurityPipelineScheduledWorkflowCadence | "">;
  daysOfWeekOptions: SelectOptions<number>;
  daysOfMonthOptions: SelectOptions<number>;
  hasScheduledWorkflows: boolean;
  isCadenceSet: boolean;
  isDisableUpdateButton: boolean;
  isLoading: boolean;
  isRunNowLoading: boolean;
  nextRunAt: string;
  onRunNow: () => void;
}

export const SecurityPipelineGithubSchedule = ({
  cadenceOptions,
  daysOfWeekOptions,
  daysOfMonthOptions,
  isCadenceSet,
  isDisableUpdateButton,
  isLoading,
  isRunNowLoading,
  nextRunAt,
  onRunNow,
}: SecurityPipelineGithubScheduleProps) => {
  const { control, setValue } =
    useFormContext<SecurityPipelineGithubAutomateFormValues>();
  const watchCadence = useWatch({ control, name: "cadence" });
  const isWeekly =
    watchCadence === SecurityPipelineScheduledWorkflowCadence.Weekly;
  const isMonthly =
    watchCadence === SecurityPipelineScheduledWorkflowCadence.Monthly;

  return (
    <Paper sx={{ p: 3 }}>
      <Flex flexDirection="column" gap={4}>
        <SecurityPipelineGithubPageTabHeading
          title="Schedule automatic fixes"
          description="Run Security pipeline on a schedule of your choosing, fixes will be tracked in your overview."
        />
        <Flex flexDirection="column">
          <Flex gap={2}>
            <Box width="50%">
              <Typography
                variant="body2"
                color="text.secondary"
                textTransform="uppercase"
                fontSize={10}
                sx={{ mb: 1 }}
              >
                How often
              </Typography>
              <Controller
                name="cadence"
                control={control}
                render={({ field }) => (
                  <Select
                    data-testid="sp-github-schedule-cadence-select"
                    disabled={isLoading}
                    value={field.value}
                    onChange={(e) => {
                      field.onChange(e.target.value);
                      setValue(
                        "day",
                        e.target.value ===
                          SecurityPipelineScheduledWorkflowCadence.Weekly
                          ? DEFAULT_WEEKLY_DAY
                          : e.target.value ===
                              SecurityPipelineScheduledWorkflowCadence.Monthly
                            ? DEFAULT_MONTHLY_DAY
                            : DEFAULT_WEEKLY_DAY,
                      );
                    }}
                    sx={{
                      "&.MuiInputBase-root": {
                        bgcolor: "code.background",
                        minWidth: 250,
                      },
                    }}
                    fullWidth
                    displayEmpty
                    renderValue={(selected: ReactNode) => {
                      const selectedCadence = cadenceOptions?.find(
                        (cadence) => cadence.value === selected,
                      );

                      if (!selected || !selectedCadence) {
                        return <>Never</>;
                      }

                      return <>{selectedCadence.label}</>;
                    }}
                  >
                    {cadenceOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </Box>
            <Typography sx={{ alignSelf: "flex-end", mb: 2 }}>on</Typography>
            <Box width="50%">
              <Typography
                variant="body2"
                color="text.secondary"
                textTransform="uppercase"
                fontSize={10}
                sx={{ mb: 1 }}
              >
                When
              </Typography>
              <Controller
                name="day"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onChange={field.onChange}
                    data-testid="sp-github-schedule-day-select"
                    sx={{
                      "&.MuiInputBase-root": {
                        bgcolor: "code.background",
                        minWidth: 250,
                      },
                    }}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 200 } } }}
                    fullWidth
                    displayEmpty
                    disabled={(!isWeekly && !isMonthly) || isLoading}
                    renderValue={(selected: ReactNode) => {
                      const selectedDay = (
                        isWeekly
                          ? daysOfWeekOptions
                          : isMonthly
                            ? daysOfMonthOptions
                            : []
                      ).find((day) => day.value === selected);

                      if (!selectedDay) {
                        return <>Choose how often</>;
                      }

                      return <>{selectedDay.label}</>;
                    }}
                  >
                    {(isWeekly
                      ? daysOfWeekOptions
                      : isMonthly
                        ? daysOfMonthOptions
                        : []
                    ).map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </Box>
          </Flex>
          {nextRunAt && isCadenceSet && (
            <Typography variant="caption" color="text.secondary">
              Your next fix is scheduled for {Format(nextRunAt)}
            </Typography>
          )}
        </Flex>
        {isCadenceSet && (
          <SecurityPipelineGithubAutomateScope isLoading={isLoading} />
        )}
      </Flex>
      <Flex gap={2} justifyContent="flex-end" mt={4}>
        <LoadingButton
          disabled={isDisableUpdateButton}
          variant="outlined"
          color="primary"
          size="large"
          data-testid="sp-github-schedule-run-now-button"
          onClick={onRunNow}
          buttonText="Run Now"
          loading={isRunNowLoading}
        />

        <LoadingButton
          disabled={isDisableUpdateButton}
          type="submit"
          variant="contained"
          color="primary"
          size="large"
          data-testid="sp-github-schedule-update-button"
          buttonText="Update"
          loading={isLoading}
        />
      </Flex>
    </Paper>
  );
};
